import { useEffect, useState } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react'

type ImageViewerDialogProps = {
  open: boolean
  imageUrl: string | null
  onOpenChange?: (open: boolean) => void
}

export function ImageViewerDialog({
  open,
  imageUrl,
  onOpenChange,
}: ImageViewerDialogProps) {
  const [zoom, setZoom] = useState(1)
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(
    null
  )
  const [offset, setOffset] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  })

  useEffect(() => {
    if (!open) {
      setZoom(1)
      setOffset({ x: 0, y: 0 })
      setDragStart(null)
    }
  }, [open])

  useEffect(() => {
    setZoom(1)
    setOffset({ x: 0, y: 0 })
    setDragStart(null)
  }, [imageUrl])

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange?.(nextOpen)
      }}
    >
      <DialogContent className='h-full max-h-[100dvh] w-full border-none bg-black rounded-none pt-0 !max-w-[100dvw]'
      closeClassName='text-white'>
        {imageUrl ? (
          <div className='flex flex-col gap-2 py-2'>
            <div className='flex items-center justify-center gap-2'>
              <Button
                size='icon'
                variant='ghost'
                onClick={() => setZoom((z) => Math.min(z + 0.25, 3))}
                className='h-9 w-9 text-white  hover:bg-white/20 hover:text-white'
              >
                <ZoomIn className='h-4 w-4' />
              </Button>
              <Button
                size='icon'
                variant='ghost'
                onClick={() => setZoom((z) => Math.max(z - 0.25, 0.5))}
                className='h-9 w-9 text-white hover:bg-white/20 hover:text-white'
              >
                <ZoomOut className='h-4 w-4' />
              </Button>
              <Button
                size='icon'
                variant='ghost'
                onClick={() => {
                  setZoom(1)
                  setOffset({ x: 0, y: 0 })
                }}
                className='h-9 w-9 text-white  hover:bg-white/20 hover:text-white'
              >
                <RotateCcw className='h-4 w-4' />
              </Button>
            </div>
            <div
              className='relative flex cursor-grab h-[calc(100dvh-74px)] items-center justify-center overflow-hidden rounded-md bg-[rgb(39,39,42)]'
              onMouseDown={(e) => {
                setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y })
              }}
              onMouseMove={(e) => {
                if (!dragStart) return
                setOffset({
                  x: e.clientX - dragStart.x,
                  y: e.clientY - dragStart.y,
                })
              }}
              onMouseUp={() => setDragStart(null)}
              onMouseLeave={() => setDragStart(null)}
            >
              <img
                src={imageUrl}
                alt='Step screenshot fullscreen'
                className='max-w-full max-h-full rounded-md select-none'
                style={{
                  transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                  transformOrigin: 'center center',
                  transition: dragStart ? 'none' : 'transform 120ms ease',
                }}
                draggable={false}
              />
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
